/**
 * Comic store - manages generated comic book pages.
 * State received from server. Gallery save/load goes through socket.
 */

import { create } from "zustand";
import { isPageDrawn, type ComicPage, type GenState } from "@amzn/a4k-creator-studio-shared";
export type { ComicPage, GenState };

interface ComicState {
  pages: ComicPage[];
  genState: GenState;
  currentPage: number;
  currentDrawingPage: number;
  /** False while layout batches are still streaming in — the final page count
   *  isn't known yet, so the UI can't show "page X of N". */
  layoutComplete: boolean;
  /** Authoritative total page count from comicLayoutComplete (cover included,
   *  same basis as pages.length), null until layout completes. The UI prefers
   *  this over pages.length so a dropped comicPagesAdded can't skew "of N". */
  totalPages: number | null;
  isLoaded: boolean;
  rawClaudeResponse: string;
  comicId: string | null;

  /** ESS save state for the current comic. `null` = not applicable (no comic
   *  or gallery-loaded). `saved` = persisted. `failed` = generation-complete
   *  fired but the one-shot ESS save failed; UI shows a retry button. */
  saveState: "saved" | "failed" | null;
  /** Most recent save-failure message to show in the banner. */
  saveError: string | null;
  /** Timestamp of the last save result (ms). Lets the UI tell a fresh
   *  server response apart from a no-op state transition. */
  saveResultAt: number;
  /** Transient "Saved to cloud" toast trigger — set by essHandlers, cleared
   *  by the toast component after it auto-dismisses. Keyed by comicId to
   *  avoid showing stale toasts on navigation. */
  savedToastComicId: string | null;

  setPages: (pages: ComicPage[]) => void;
  addPages: (pages: ComicPage[]) => void;
  setPageImage: (pageNumber: number, imageBase64: string) => void;
  setPageImageId: (pageNumber: number, imageId: string) => void;
  /** Flag a page whose image was permanently blocked by a content check — it
   *  renders an "oops" placeholder instead of an illustration. */
  setPageImageError: (pageNumber: number) => void;
  setGenState: (state: GenState) => void;
  setCurrentPage: (page: number) => void;
  setCurrentDrawingPage: (page: number) => void;
  /** Mark layout complete and record the authoritative content-page count. */
  setLayoutComplete: (totalPages: number) => void;
  setRawClaudeResponse: (text: string) => void;
  setPageDebug: (pageNumber: number, prompt: string, refImages: string[]) => void;
  clearAll: () => void;
  /** Replace all comic state (used by fullState on reconnect) */
  replaceAll: (data: { comicId: string; pages: ComicPage[]; genState: GenState; rawClaudeResponse?: string; layoutComplete?: boolean }) => void;

  /** Called by essHandlers on comicSaved / comicSaveFailed. */
  setSaveResult: (comicId: string, result: { saved: true } | { saved: false; error: string }) => void;
  /** Dismiss the transient "Saved" toast once it's been shown. */
  clearSavedToast: () => void;
  /** Attach narration audio and speech marks to a page after generation. */
  updatePageAudio: (pageNumber: number, audioBase64: string, speechMarks?: ComicPage["speechMarks"]) => void;
}

export const useComicStore = create<ComicState>((set, get) => ({
  pages: [],
  genState: "idle",
  currentPage: 0,
  currentDrawingPage: 0,
  layoutComplete: false,
  totalPages: null,
  isLoaded: false,
  rawClaudeResponse: "",
  comicId: null,
  saveState: null,
  saveError: null,
  saveResultAt: 0,
  savedToastComicId: null,

  setPages: (pages) => {
    set({ pages, currentPage: 0 });
  },

  addPages: (pages) => {
    set((s) => {
      const existing = new Set(s.pages.map((p) => p.pageNumber));
      const fresh = pages.filter((p) => !existing.has(p.pageNumber));
      if (fresh.length === 0) return s;
      return { pages: [...s.pages, ...fresh] };
    });
  },

  setPageImage: (pageNumber, imageBase64) => {
    set((s) => ({
      pages: s.pages.map((p) =>
        p.pageNumber === pageNumber ? { ...p, imageBase64 } : p,
      ),
    }));
  },

  setPageImageId: (pageNumber, imageId) => {
    set((s) => ({
      pages: s.pages.map((p) =>
        p.pageNumber === pageNumber ? { ...p, imageId } : p,
      ),
    }));
  },

  setPageImageError: (pageNumber) => {
    set((s) => ({
      pages: s.pages.map((p) =>
        p.pageNumber === pageNumber ? { ...p, imageError: true } : p,
      ),
    }));
  },

  updatePageAudio: (pageNumber, audioBase64, speechMarks) => {
    set((s) => ({
      pages: s.pages.map((p) =>
        p.pageNumber === pageNumber ? { ...p, audioBase64, ...(speechMarks ? { speechMarks } : {}) } : p,
      ),
    }));
  },

  setGenState: (genState) =>
    set(genState === "generating_prompts" ? { genState, layoutComplete: false, totalPages: null } : { genState }),
  setCurrentPage: (currentPage) => set({ currentPage }),
  setCurrentDrawingPage: (currentDrawingPage) => set({ currentDrawingPage }),
  setLayoutComplete: (totalPages) => set({ layoutComplete: true, totalPages }),

  setRawClaudeResponse: (text) => {
    set({ rawClaudeResponse: text });
  },

  setPageDebug: (pageNumber, prompt, refImages) => {
    set((s) => ({
      pages: s.pages.map((p) =>
        p.pageNumber === pageNumber ? { ...p, debugPrompt: prompt, debugRefImages: refImages } : p,
      ),
    }));
  },

  clearAll: () => {
    set({ pages: [], genState: "idle", currentPage: 0, currentDrawingPage: 0, layoutComplete: false, totalPages: null, rawClaudeResponse: "", comicId: null, saveState: null, saveError: null, saveResultAt: 0, savedToastComicId: null });
  },

  replaceAll: (data) => {
    // When generation is in progress, infer currentDrawingPage from the first
    // page that doesn't have an image yet (that's the one currently being drawn).
    let drawingPage = 0;
    if (data.genState === "generating_pages" && data.pages.length > 0) {
      const sorted = [...data.pages].sort((a, b) => a.pageNumber - b.pageNumber);
      const firstUnfinished = sorted.find((p) => !isPageDrawn(p));
      drawingPage = firstUnfinished ? firstUnfinished.pageNumber : sorted[sorted.length - 1].pageNumber;
    }
    // fullState carries the full page set, so pages.length is authoritative here.
    // Default an in-progress comic to "layout not complete" when the flag is absent.
    const layoutComplete = data.genState !== "generating_pages" || (data.layoutComplete ?? false);
    set({
      comicId: data.comicId,
      pages: data.pages,
      genState: data.genState,
      layoutComplete,
      totalPages: layoutComplete && data.pages.length > 0 ? data.pages.length : null,
      rawClaudeResponse: data.rawClaudeResponse ?? "",
      currentPage: 0,
      currentDrawingPage: drawingPage,
      isLoaded: true,
      // fullState comes from Redis; if the comic is in fullState it means the
      // generation completed recently. We don't know save-state from fullState
      // alone — leave null and let any subsequent comicSaved/comicSaveFailed
      // event set it. If neither arrives, the UI shows no banner.
      saveState: null,
      saveError: null,
      saveResultAt: 0,
    });
  },

  setSaveResult: (comicId, result) => {
    // Ignore results for a different comicId than the one currently loaded —
    // can happen if the user regenerated while a save was in flight.
    if (get().comicId !== comicId) return;
    // Always bump saveResultAt so consumers can distinguish "new response"
    // from "same state as before retry" (important when a retry fails with
    // the same error message — without the bump, the UI can't tell that the
    // server actually responded).
    const saveResultAt = Date.now();
    if (result.saved) {
      set({ saveState: "saved", saveError: null, savedToastComicId: comicId, saveResultAt });
    } else {
      set({ saveState: "failed", saveError: result.error, saveResultAt });
    }
  },

  clearSavedToast: () => set({ savedToastComicId: null }),
}));

if (__DEV__ && typeof window !== "undefined") {
  (window as any).useComicStore = useComicStore;
}
